import { Injectable } from "@angular/core";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import { TwoFactorAuthMethod } from "@obg/ngx.core";
import { TelemetryService } from "@obg/ngx.global.evo";

import {
	TwoFactorAuthDialogStep,
	TwoFactorAuthFlow,
	TwoFactorAuthMethodType,
	TwoFactorAuthRemoveType,
} from "./two-factor-auth.const";
import { TwoFactorAuthEnabledMethods } from "./two-factor-auth.model";

/**
 * Service for handling every Two Factor Authentication related
 * telemetry calls.
 *
 * See also:
 * - [Verification Tag Map (2FA)]( /wiki/spaces/OPTI/pages/232253350/Verification+Tag+Map+2FA)
 * - [Desktop Journey - 2FA]( /wiki/spaces/OPTI/pages/232250752/Desktop+Journey+-+2FA)
 * - [Mobile Journey - 2FA]( /wiki/spaces/OPTI/pages/232249117/Mobile+Journey+-+2FA)
 */
@Injectable({
	providedIn: "root",
})
export class TwoFactorAuthTelemetryService {
	constructor(private telemetryService: TelemetryService) {}

	trackLoginPasswordOpen({ method }: { method: TwoFactorAuthMethod | undefined }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormOpen,
			FormName: "TwoFA_Enter_Login_Password",
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.SetupMethodFlow,
				method: method,
			}),
		});
	}

	trackForgotPasswordOnClick({ method }: { method: TwoFactorAuthMethod | undefined }) {
		this.telemetryService.track({
			event: GtmEventAction.LinkClick,
			ItemClicked: "Forgot your password",
			GAEventName: "TwoFA_Enter_Login_Password",
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.SetupMethodFlow,
				method: method,
			}),
		});
	}

	trackPasswordInputChange({ method }: { method: TwoFactorAuthMethod | undefined }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormFieldChange,
			FormName: "TwoFA_Enter_Login_Password",
			FieldName: "Password",
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.SetupMethodFlow,
				method: method,
			}),
		});
	}

	trackVerifyPassword({ method }: { method: TwoFactorAuthMethod | undefined }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormSubmit,
			FormName: "TwoFA_Enter_Login_Password",
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.SetupMethodFlow,
				method: method,
				removeMethodType: undefined,
			}),
		});
	}

	trackVerificationMethodSelectorOpened() {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormOpen,
			FormName: "TwoFA_Authentication_Methods",
			SubArea: "TwoFA_Setup",
		});
	}

	trackSkipForNowClicked() {
		this.telemetryService.track({
			event: GtmEventAction.LinkClick,
			ItemClicked: "SkipForNow",
			GAEventName: "TwoFA_Authentication_Methods",
			SubArea: "TwoFA_Setup",
		});
	}

	trackEmailMethodSelected() {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "TwoFA_Authentication_Methods",
			ItemClicked: "Set up Email",
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.SetupMethodFlow,
				method: TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod,
			}),
		});
	}

	trackSMSMethodSelected() {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "TwoFA_Authentication_Methods",
			ItemClicked: "Set up SMS",
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.SetupMethodFlow,
				method: TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod,
			}),
		});
	}

	trackLoginPasswordVerifiedSuccessfully({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormSuccess,
			FormName: "TwoFA_Enter_Login_Password",
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.LoginFlow,
				method,
			}),
		});
	}

	trackLoginPasswordVerificationFailed({ error, method }: { error: string; method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormFailed,
			FormName: "TwoFA_Enter_Login_Password",
			Error: error,
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.LoginFlow,
				method,
			}),
		});
	}

	trackVerificationCodeSuccess({
		method,
		enabledMethods,
		operationType,
	}: {
		method: TwoFactorAuthMethod;
		enabledMethods: TwoFactorAuthEnabledMethods[];
		operationType: TwoFactorAuthMethodType;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormSuccess,
			FormName: getVerificationCodeFormNameForResult(method, TwoFactorAuthFlow.SetupMethodFlow),
			EventLabel: getEventLabelByMethod(method, enabledMethods),
			SubArea: getSubAreaForOperationType({ operationType, method }),
		});
	}

	trackVerificationCodeFailure({
		method,
		enabledMethods,
		operationType,
		error,
	}: {
		method: TwoFactorAuthMethod;
		enabledMethods: TwoFactorAuthEnabledMethods[];
		operationType: TwoFactorAuthMethodType;
		error: string;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormFailed,
			Error: error,
			FormName: getVerificationCodeFormName(method),
			EventLabel: getEventLabelByMethod(method, enabledMethods),
			SubArea: getSubAreaForOperationType({ operationType, method }),
		});
	}

	trackLoginWithTwoFactorAuthSuccess({
		method,
		suspiciousFlow,
	}: {
		method: TwoFactorAuthMethod;
		suspiciousFlow: boolean;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormSuccess,
			FormName: getVerificationCodeFormNameForResult(method, TwoFactorAuthFlow.LoginFlow, suspiciousFlow),
			EventLabel: getEventLabelByMethod(method),
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.LoginFlow,
				method,
			}),
		});
	}

	trackLoginWithTwoFactorAuthFail({
		method,
		error,
		suspiciousFlow,
	}: {
		method: TwoFactorAuthMethod;
		error: string;
		suspiciousFlow: boolean;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormFailed,
			FormName: getVerificationCodeFormName(method, suspiciousFlow),
			EventLabel: getEventLabelByMethod(method),
			Error: error,
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.LoginFlow,
				method,
			}),
		});
	}

	trackTurningOffSingleMethodSuccess({
		operationType,
		method,
	}: {
		operationType: TwoFactorAuthMethodType;
		method: TwoFactorAuthMethod;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormSuccess,
			FormName: "TwoFA_Disable_TwoFA_Authentication",
			EventLabel: getEventLabelByMethod(method),
			SubArea: getSubAreaForOperationType({ operationType, method }),
		});
	}

	trackTurningOffBothMethodsSuccess() {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormSuccess,
			FormName: "TwoFA_Disable_TwoFA_Authentication",
			SubArea: "TwoFA_Disable_Both",
		});
	}

	openRemoveMethodConfirmation({ removeMethodType }: { removeMethodType: TwoFactorAuthRemoveType }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormOpen,
			FormName: getDisableFormNameByRemoveMethodType(removeMethodType),
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.RemoveMethodFlow,
				removeMethodType: removeMethodType,
			}),
		});
	}

	registrationOpenVerificationCodeContainer({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormOpen,
			FormName: "Registration_" + getVerificationCodeFormNameByMethod(method),
			SubArea: "Registration",
		});
	}

	myAccountOpenVerificationCodeContainer({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormOpen,
			FormName: "MyAccount_" + getVerificationCodeFormNameByMethod(method),
			SubArea: "MyAccount",
		});
	}

	openVerificationCodeContainer({
		method,
		authFlow,
		removeMethodType,
		suspiciousFlow,
	}: {
		method: TwoFactorAuthMethod;
		authFlow: TwoFactorAuthFlow;
		removeMethodType: TwoFactorAuthRemoveType | undefined;
		suspiciousFlow: boolean;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormOpen,
			FormName: getVerificationCodeFormName(method, suspiciousFlow),
			SubArea: getSubArea({ authFlow: authFlow, method, removeMethodType: removeMethodType }),
		});
	}

	submitRegistrationVerificationSuccess({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "Verification",
			itemClicked: getItemName(method),
			SubArea: "Registration",
		});
	}

	submitMyAccountVerificationSuccess({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "Verification",
			itemClicked: getItemName(method),
			SubArea: "MyAccount",
		});
	}

	submitVerificationCodeContainer({
		method,
		authFlow,
		removeMethodType,
		suspiciousFlow,
	}: {
		method: TwoFactorAuthMethod;
		authFlow: TwoFactorAuthFlow;
		removeMethodType: TwoFactorAuthRemoveType | undefined;
		suspiciousFlow: boolean;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormSubmit,
			FormName: getVerificationCodeFormName(method, suspiciousFlow),
			SubArea: getSubArea({
				authFlow: authFlow,
				method: method,
				removeMethodType,
			}),
		});
	}

	rememberDeviceChanged({
		method,
		authFlow,
		removeMethodType,
	}: {
		method: TwoFactorAuthMethod;
		authFlow: TwoFactorAuthFlow;
		removeMethodType: TwoFactorAuthRemoveType | undefined;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.LinkClick,
			ItemClicked: "RememberMeOnThisBrowser",
			GAEventName: getVerificationCodeFormName(method),
			SubArea: getSubArea({
				authFlow: authFlow,
				method: method,
				removeMethodType: removeMethodType,
			}),
		});
	}

	registrationChangeMethodClicked({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "Verification",
			ItemClicked: getItemNameForChangingMethod(method),
			SubArea: "Registration",
		});
	}

	changeMethodClicked({
		method,
		authFlow,
		removeMethodType,
	}: {
		method: TwoFactorAuthMethod;
		authFlow: TwoFactorAuthFlow;
		removeMethodType: TwoFactorAuthRemoveType | undefined;
		suspiciousFlow: boolean;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: getVerificationCodeFormName(method),
			ItemClicked: getItemNameForChangingMethod(method),
			SubArea: getSubArea({
				authFlow: authFlow,
				method: method,
				removeMethodType: removeMethodType,
			}),
		});
	}

	registrationResendCodeButtonClicked({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.LinkClick,
			ItemClicked: getItemNameForResendCode(method),
			SubArea: "Registration",
		});
	}

	myAccountResendCodeButtonClicked({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.LinkClick,
			ItemClicked: getItemNameForResendCode(method),
			SubArea: "MyAccount",
		});
	}

	resendCodeButtonClicked({
		method,
		authFlow,
		removeMethodType,
		suspiciousFlow,
	}: {
		method: TwoFactorAuthMethod;
		authFlow: TwoFactorAuthFlow;
		removeMethodType: TwoFactorAuthRemoveType | undefined;
		suspiciousFlow: boolean;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.LinkClick,
			ItemClicked: "ResendCode",
			GAEventName: getVerificationCodeFormName(method, suspiciousFlow),
			SubArea: getSubArea({
				authFlow: authFlow,
				method: method,
				removeMethodType: removeMethodType,
			}),
		});
	}

	trackRegistrationVerificationCodeChange({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormFieldChange,
			FormName: "Registration_" + getVerificationCodeFormNameByMethod(method),
			FieldName: "Verification Code",
			SubArea: "Registration",
		});
	}

	trackMyAccountVerificationCodeChange({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormFieldChange,
			FormName: "MyAccount_" + getVerificationCodeFormNameByMethod(method),
			FieldName: "Verification Code",
			SubArea: "MyAccount",
		});
	}

	trackVerificationCodeChange({
		method,
		authFlow,
		removeMethodType,
		suspiciousFlow,
	}: {
		method: TwoFactorAuthMethod;
		authFlow: TwoFactorAuthFlow;
		removeMethodType: TwoFactorAuthRemoveType | undefined;
		suspiciousFlow: boolean;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.GenericFormFieldChange,
			FormName: getVerificationCodeFormName(method, suspiciousFlow),
			FieldName: "Verification Code",
			SubArea: getSubArea({
				authFlow: authFlow,
				method: method,
				removeMethodType: removeMethodType,
			}),
		});
	}

	trackConfirmRemoval({ removeMethodType }: { removeMethodType: TwoFactorAuthRemoveType }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: getDisableFormNameByRemoveMethodType(removeMethodType),
			ItemClicked: getItemNameForRemovalButton(removeMethodType),
			SubArea: getSubArea({
				authFlow: TwoFactorAuthFlow.RemoveMethodFlow,
				removeMethodType: removeMethodType,
			}),
		});
	}

	trackContactSupportClick({
		method,
		authFlow,
		removeMethodType,
		step,
	}: {
		method: TwoFactorAuthMethod | undefined;
		authFlow: TwoFactorAuthFlow;
		removeMethodType: TwoFactorAuthRemoveType | undefined;
		step: TwoFactorAuthDialogStep;
	}) {
		this.telemetryService.track({
			event: GtmEventAction.LinkClick,
			ItemClicked: "Contact Support",
			GAEventName: getVerificationCodeFormName(method, undefined, step),
			SubArea: getSubArea({
				authFlow: authFlow,
				method: method,
				removeMethodType: removeMethodType,
				step: step,
			}),
		});
	}

	trackDialogBackdropClick() {
		this.telemetryService.track({
			event: GtmEventAction.ClosePopup,
			ItemClicked: "Two_FA_Backdrop",
		});
	}

	trackTurnOffTwoFaClick() {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "My_Account",
			ItemClicked: "Disable TwoFA",
			SubArea: "TwoFA_Authentication",
		});
	}

	trackRemoveOrOpenSetupButtonOnClick({ method, isEnabled }: { method: TwoFactorAuthMethod; isEnabled: boolean }) {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "My_Account",
			ItemClicked: getRemoveOrSetupItemName({ method: method, isEnabled: isEnabled }),
			SubArea: "TwoFA_Authentication",
		});
	}

	trackEnableTwoFactorAuth() {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "My_Account",
			ItemClicked: "Enable TwoFA",
			SubArea: "TwoFA_Authentication",
		});
	}

	trackForgetDeviceClick() {
		this.telemetryService.track({
			event: GtmEventAction.GenericButtonClick,
			FormName: "My_Account",
			ItemClicked: "ForgetBrowser",
			SubArea: "TwoFA_Authentication",
		});
	}

	trackSetPrimaryOnClick({ method }: { method: TwoFactorAuthMethod }) {
		this.telemetryService.track({
			event: GtmEventAction.LinkClick,
			GAEventName: "TwoFA_SetAsPrimary",
			ItemClicked: getPrimaryButtonName(method),
			SubArea: "TwoFA_Authentication",
		});
	}
}

function getVerificationCodeFormNameByMethod(method: TwoFactorAuthMethod) {
	switch (method) {
		case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
			return "Email_Verification_Code";
		case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
			return "SMS_Verification_Code";
	}
}

function getVerificationCodeFormName(
	method: TwoFactorAuthMethod | undefined,
	suspiciousFlow = false,
	step?: TwoFactorAuthDialogStep
) {
	if (suspiciousFlow) {
		return "TwoFA_Suspicious_Login_Enter_Verification_Code";
	} else if (method) {
		return method === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
			? "TwoFA_Email_Verification_Code"
			: "TwoFA_SMS_Verification_Code";
	} else if (step === TwoFactorAuthDialogStep.LoginPassword) {
		return "TwoFA_Enter_Login_Password";
	} else if (step === TwoFactorAuthDialogStep.SelectMethod || step === TwoFactorAuthDialogStep.ReSelectMethod) {
		return "TwoFA_Authentication_Methods";
	}
	return "TwoFA_Unknown_Form";
}

function getVerificationCodeFormNameForResult(
	method: TwoFactorAuthMethod | undefined,
	authFlow: TwoFactorAuthFlow,
	suspiciousFlow = false
) {
	if (authFlow === TwoFactorAuthFlow.LoginFlow) {
		return (
			(suspiciousFlow ? "TwoFA_Suspicious_Login_" : "TwoFA_Login_") +
			(method === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod ? "Email_" : "Mobile_SMS_") +
			"Authentication"
		);
	}
	return method === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod
		? "TwoFA_Setup_Email_Authentication"
		: "TwoFA_Setup_Mobile_SMS_Authentication";
}

function getEventLabelByMethod(method: TwoFactorAuthMethod, enabledMethods?: TwoFactorAuthEnabledMethods[]) {
	if (enabledMethods && enabledMethods.length > 1) {
		return "Email, SMS";
	} else if (method === TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod) {
		return "Email";
	}
	return "SMS";
}

function getDisableFormNameByRemoveMethodType(removeMethodType: TwoFactorAuthRemoveType) {
	switch (removeMethodType) {
		case TwoFactorAuthRemoveType.RemoveEmailMethod:
			return "TwoFA_Disable_Email_Authentication";
		case TwoFactorAuthRemoveType.RemoveSmsMethod:
			return "TwoFA_Disable_SMS_Authentication";
		case TwoFactorAuthRemoveType.RemoveBothMethod:
			return "TwoFA_Disable_TwoFA_Authentication";
	}
}

function getSubArea({
	authFlow,
	method = undefined,
	removeMethodType = undefined,
	step,
}: {
	authFlow: TwoFactorAuthFlow;
	method?: TwoFactorAuthMethod | undefined;
	removeMethodType?: TwoFactorAuthRemoveType | undefined;
	step?: TwoFactorAuthDialogStep;
}) {
	switch (authFlow) {
		case TwoFactorAuthFlow.LoginFlow:
			switch (method) {
				case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
					return "TwoFA_Login_Email";
				case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
					return "TwoFA_Login_SMS";
				case undefined:
					return "TwoFA_Login";
			}
		case TwoFactorAuthFlow.SetupMethodFlow:
			switch (method) {
				case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
					return "TwoFA_Enable_Email";
				case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
					return "TwoFA_Enable_SMS";
				case undefined:
					return step === TwoFactorAuthDialogStep.SelectMethod ||
						step === TwoFactorAuthDialogStep.ReSelectMethod
						? "TwoFA_Setup"
						: "Setup_Login";
			}
		case TwoFactorAuthFlow.RemoveMethodFlow:
			switch (removeMethodType!) {
				case TwoFactorAuthRemoveType.RemoveEmailMethod:
					return "TwoFA_Disable_Email";
				case TwoFactorAuthRemoveType.RemoveSmsMethod:
					return "TwoFA_Disable_SMS";
				case TwoFactorAuthRemoveType.RemoveBothMethod:
					return "TwoFA_Disable_Both";
			}
		case TwoFactorAuthFlow.ChangePhoneFlow:
			return "MyAccount";
	}
}

function getSubAreaForOperationType({
	operationType,
	method,
}: {
	operationType: TwoFactorAuthMethodType;
	method: TwoFactorAuthMethod;
}) {
	switch (operationType) {
		case TwoFactorAuthMethodType.Setup:
			switch (method) {
				case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
					return "TwoFA_Setup_Email";
				case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
					return "TwoFA_Setup_SMS";
			}
		case TwoFactorAuthMethodType.Remove:
			switch (method) {
				case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
					return "TwoFA_Disable_Email";
				case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
					return "TwoFA_Disable_SMS";
			}
		case TwoFactorAuthMethodType.RemoveAll:
			return "TwoFA_Disable_Both";
	}
	throw new Error(`Invalid operation type and method combination: ${operationType}, ${method}`);
}

function getItemName(method: TwoFactorAuthMethod) {
	switch (method) {
		case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
			return "Verify Email";
		case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
			return "Verify SMS";
	}
}

function getItemNameForChangingMethod(method: TwoFactorAuthMethod) {
	switch (method) {
		case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
			return "Verify with SMS instead";
		case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
			return "Verify with Email instead";
	}
}

function getItemNameForResendCode(method: TwoFactorAuthMethod) {
	switch (method) {
		case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
			return "Email Verification_Resend Code";
		case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
			return "SMS Verification_Resend Code";
	}
}

function getItemNameForRemovalButton(removeMethodType: TwoFactorAuthRemoveType) {
	switch (removeMethodType) {
		case TwoFactorAuthRemoveType.RemoveEmailMethod:
			return "Turn off Email";
		case TwoFactorAuthRemoveType.RemoveSmsMethod:
			return "Turn off SMS";
		case TwoFactorAuthRemoveType.RemoveBothMethod:
			return "Turn off TwoFA";
	}
}

function getRemoveOrSetupItemName({ method, isEnabled }: { method: TwoFactorAuthMethod; isEnabled: boolean }) {
	if (isEnabled) {
		switch (method) {
			case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
				return "Turn off Email";
			case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
				return "Turn off SMS";
		}
	} else {
		switch (method) {
			case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
				return "Set up Email";
			case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
				return "Set up SMS";
		}
	}
}

function getPrimaryButtonName(method: TwoFactorAuthMethod) {
	switch (method) {
		case TwoFactorAuthMethod.EmailTwoFactorAuthenticationMethod:
			return "Email";
		case TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod:
			return "SMS";
	}
}
